import React from "react";
import { useI18n } from "../i18n";
import { CONTACT } from "../../api";
import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";

const PHONES = [
  {
    id: "do",
    flag: "🇩🇴",
    region: "Dominican Republic",
    pretty: "+1 849 919 7111",
    raw: "+18499197111",
    isPrimary: true,
  },
  {
    id: "us",
    flag: "🇺🇸",
    region: "United States",
    pretty: "+1 347 397 8882",
    raw: "+13473978882",
    isPrimary: false,
  },
];

export default function ContactHelp() {
  const { t } = useI18n();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-10">
        <p className="dams-brand-sub text-red-500 mb-2">{t("contact.eyebrow")}</p>
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight" data-testid="contact-title">
          {t("contact.title")}
        </h1>
        <p className="text-gray-600 mt-3 max-w-xl mx-auto">{t("contact.subtitle")}</p>
      </div>

      {/* WhatsApp banner */}
      <a
        href={`${CONTACT.whatsappLink}?text=${encodeURIComponent(
          "Hi DAMS Car Rental, I need some help with my reservation."
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-whatsapp-banner"
        className="block group mb-8"
      >
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
            <MessageCircle size={28} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.18em] opacity-80 font-semibold mb-1">
              {t("contact.fastest")}
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold">{t("contact.whatsappTitle")}</h2>
            <p className="text-emerald-50/95 mt-1 text-sm md:text-base">
              {t("contact.whatsappDesc")}
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-2.5 rounded-full text-sm font-bold group-hover:bg-white/25 transition-colors">
            {t("contact.whatsappCta")} →
          </div>
        </div>
      </a>

      {/* Phones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {PHONES.map((p) => (
          <a
            key={p.id}
            href={`tel:${p.raw}`}
            data-testid={`contact-phone-${p.id}`}
            className="card-soft p-6 group hover:border-red-500/30 hover:-translate-y-0.5 transition-all"
            style={{ border: "1px solid #f3f4f6" }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl leading-none">{p.flag}</span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                    {p.region}
                  </p>
                  {p.isPrimary && (
                    <span className="badge badge-amber text-[10px] mt-1">{t("contact.primary")}</span>
                  )}
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors">
                <Phone size={16} />
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900">
              {p.pretty}
            </p>
            <p className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1.5">
              <Clock size={11} /> {t("contact.hours")}
            </p>
          </a>
        ))}
      </div>

      {/* Email + Address */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <a
          href={`mailto:${CONTACT.email}`}
          data-testid="contact-email"
          className="card-soft p-6 flex items-start gap-4 group hover:border-red-500/30 hover:-translate-y-0.5 transition-all"
          style={{ border: "1px solid #f3f4f6" }}
        >
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 group-hover:bg-red-600 group-hover:text-white transition-colors">
            <Mail size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
              {t("contact.email")}
            </p>
            <p className="font-extrabold text-lg break-all">{CONTACT.email}</p>
            <p className="text-xs text-gray-500 mt-1">{t("contact.emailDesc")}</p>
          </div>
        </a>

        <div className="card-soft p-6 flex items-start gap-4" style={{ border: "1px solid #f3f4f6" }}>
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <MapPin size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
              {t("contact.headquarters")}
            </p>
            <p className="font-extrabold text-lg leading-tight">Bella Vista</p>
            <p className="text-sm text-gray-600">Santo Domingo, Dominican Republic</p>
          </div>
        </div>
      </div>

      {/* Bottom card */}
      <div className="mt-10 rounded-2xl bg-gray-900 text-white p-8 text-center">
        <h3 className="text-xl md:text-2xl font-extrabold mb-2">{t("contact.alreadyBookedTitle")}</h3>
        <p className="text-gray-300 text-sm max-w-md mx-auto mb-5">
          {t("contact.alreadyBookedDesc")}
        </p>
        <a
          href="/my-bookings"
          className="pill-btn primary inline-block"
          style={{ padding: "0.75rem 1.5rem" }}
        >
          {t("contact.viewBookings")}
        </a>
      </div>
    </div>
  );
}
